import { Component, OnDestroy, OnInit, DestroyRef, inject, ChangeDetectorRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, NavigationEnd } from '@angular/router';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { CheckoutActions } from '@pages/checkout-app/checkout-store/actions';
import * as fromCheckoutReducer from '@pages/checkout-app/checkout-store/reducers/checkout.reducer';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { ExtensionComponentType } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { Shop } from '@services/shops/shop.model';
import { ShopService } from '@services/shops/shop.service';
import { Observable, Subject } from 'rxjs';
import { takeUntil, filter, take, first, skip } from 'rxjs/operators';
import { CheckoutMetricsComponent } from './components/shared/checkout-metrics/checkout-metrics.component';
import { ToastOptions } from '@app/storybook/services/toast/toast-types';
import { ExtensionsUsingIdInUrl, TypeToRouteSlugMap } from './types';
import { LayoutService } from '@app/storybook/services/layout/layout.service';
@Component({
  selector: 'checkout-extensions',
  templateUrl: './extensions.component.html',
  styleUrls: ['./extensions.component.scss'],
  standalone: false,
})
export class CheckoutExtensionsComponent implements OnInit, OnDestroy {
  @onDestroyComplete() destroy$ = new Subject();
  shop$: Observable<Shop>;
  type: ExtensionComponentType;
  loadedExtension$ = this.store.select(fromCheckoutReducer.selectActiveExtension);
  extensionsList$ = this.store.select(fromCheckoutReducer.selectExtensionsForList);
  extensionsListLoading$ = this.store.select(fromCheckoutReducer.selectExtensionsListLoading);
  extensionDetailLoading$ = this.store.select(fromCheckoutReducer.selectExtensionDetailLoading);
  destroyRef = inject(DestroyRef);
  loadingExtension = false;
  enableMetrics = false;
  returnUrl: string | null = null;
  private get routeSlugToTypeMap(): Record<string, ExtensionComponentType> {
    return Object.fromEntries(
      Object.entries(TypeToRouteSlugMap).map(([key, value]) => [value, key as ExtensionComponentType]),
    ) as Record<string, ExtensionComponentType>;
  }

  constructor(
    private shopService: ShopService,
    private store: Store<fromCheckoutReducer.State>,
    private actions$: Actions,
    public extensionsService: ExtensionsService,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutService: LayoutService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.shop$ = this.shopService.shop$;
    this.returnUrl = this.activatedRoute.snapshot.queryParams['returnUrl'] || null;
    const fromGroup = this.activatedRoute.snapshot.queryParams['fromGroup'];
    if (fromGroup) {
      this.returnUrl = `/convert/extension-groups/${fromGroup}`;
    }
    this.detectSuccess();
    this.detectFailure();
    this.extensionsService.validateExtensionsRequirements();
    this.extensionsService.initDynamicFieldsAutocomplete();
    this.handleNavigation();
    this.subscribeToExtensionType();
  }

  ngOnDestroy() {}

  detectFailure(): void {
    this.actions$
      .pipe(
        takeUntil(this.destroy$),
        ofType(
          CheckoutActions.loadExtensionByIdFailure,
          CheckoutActions.deleteExtensionFailure,
          CheckoutActions.loadExtensionsListFailure,
          CheckoutActions.updateExtensionFailure,
          CheckoutActions.saveExtensionFailure,
          CheckoutActions.duplicateExtensionFailure,
        ),
      )
      .subscribe((res) => {
        const stateMsg = res?.type?.split(']')[1];
        const options: ToastOptions = { message: stateMsg, mode: 'danger' };
        this.extensionsService.createToast(options);
      });
  }

  detectSuccess(): void {
    this.actions$.pipe(takeUntil(this.destroy$), ofType(CheckoutActions.saveExtensionSuccess)).subscribe(() => {
      this.store.dispatch(CheckoutActions.loadExtensionsListLightweight());
      this.store.dispatch(CheckoutActions.resetActiveExtension());
      this.extensionsService.resetExtensionState();
      const options: ToastOptions = { message: 'Extension saved successfully', mode: 'info' };
      this.extensionsService.createToast(options);
      if (this.returnUrl) {
        this.router.navigateByUrl(this.returnUrl);
      } else {
        this.router.navigate(['/convert/extensions']);
      }
    });

    // Handle updateExtensionSuccess only when editing a form (not from list toggles)
    this.actions$
      .pipe(
        takeUntil(this.destroy$),
        ofType(CheckoutActions.updateExtensionSuccess),
        filter(() => {
          // Only reload/navigate if we're on an edit route, not on the list
          const currentUrl = this.router.url;
          return !currentUrl.endsWith('/convert/extensions') && currentUrl.includes('/convert/extensions/');
        }),
      )
      .subscribe(() => {
        this.store.dispatch(CheckoutActions.loadExtensionsListLightweight());
        this.store.dispatch(CheckoutActions.resetActiveExtension());
        this.extensionsService.resetExtensionState();
        const options: ToastOptions = { message: 'Extension saved successfully', mode: 'info' };
        this.extensionsService.createToast(options);
        if (this.returnUrl) {
          this.router.navigateByUrl(this.returnUrl);
        } else {
          this.router.navigate(['/convert/extensions']);
        }
      });

    this.actions$.pipe(takeUntil(this.destroy$), ofType(CheckoutActions.loadExtensionByIdSuccess)).subscribe(() => {
      setTimeout(() => {
        this.layoutService.scrollTo$.next({ top: 0, left: 0, behavior: 'smooth' });
      });
      this.loadingExtension = false;
      const options = { message: 'Extension loaded successfully', mode: 'info' } as ToastOptions;
      this.extensionsService.createToast(options);
    });

    this.actions$
      .pipe(takeUntil(this.destroy$), ofType(CheckoutActions.duplicateExtensionSuccess))
      .subscribe(({ extension }) => {
        const options: ToastOptions = {
          message: 'Extension duplication in progress, edit or save to continue',
          mode: 'info',
        };
        this.extensionsService.createToast(options);
        const routeSlug = TypeToRouteSlugMap[extension.type];
        if (routeSlug) {
          this.router.navigate(['/convert/extensions', routeSlug, 'new']);
        }
      });
  }

  cancel() {
    this.store.dispatch(CheckoutActions.resetActiveExtension());
    this.store.dispatch(CheckoutActions.resetLiveExampleTemplateView());
    this.extensionsService.resetExtensionState();
    if (this.returnUrl) {
      this.router.navigateByUrl(this.returnUrl);
    } else {
      this.router.navigate(['/convert/extensions']);
    }
  }

  get isSaveDisabled(): boolean {
    return !this.extensionsService.getFormValid();
  }

  handleNavigation() {
    this.checkAndLoadExtensionFromUrl();
    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd),
        skip(1),
      )
      .subscribe(() => {
        this.checkAndLoadExtensionFromUrl();
      });
  }

  private getExtensionTypeFromRoute(): { type: ExtensionComponentType; paramValue: string } | null {
    const childRoute = this.activatedRoute.firstChild;
    if (!childRoute) return null;
    const routePath = childRoute.snapshot.routeConfig?.path?.split('/')[0];
    if (!routePath) return null;
    const extensionType = this.routeSlugToTypeMap[routePath];
    if (!extensionType) return null;
    const usesIdInUrl = ExtensionsUsingIdInUrl.includes(extensionType);
    const paramValue = usesIdInUrl
      ? childRoute.snapshot.params['componentId']
      : childRoute.snapshot.params['usageToken'];
    if (!paramValue) return null;
    return { type: extensionType, paramValue };
  }

  private loadExtensionById(extensionId: number): void {
    this.loadedExtension$.pipe(first()).subscribe({
      next: (activeExt) => {
        if (!activeExt || activeExt.id !== extensionId) {
          this.loadExtension(extensionId);
        }
      },
      error: () => {
        this.router.navigate(['/convert/extensions']);
        const options: ToastOptions = { message: 'Extension failed to load', mode: 'danger' };
        this.extensionsService.createToast(options);
      },
    });
  }

  private loadExtensionByUsageToken(usageToken: string): void {
    this.extensionsList$
      .pipe(
        filter((extensions) => extensions?.length > 0),
        take(1),
      )
      .subscribe({
        next: (extensions) => {
          const extension = extensions.find((ext) => ext.usageToken === usageToken);
          if (extension) {
            this.loadExtensionById(extension.id);
          }
        },
        error: () => {
          this.router.navigate(['/convert/extensions']);
          const options: ToastOptions = { message: 'Extension failed to load', mode: 'danger' };
          this.extensionsService.createToast(options);
        },
      });
  }
  checkAndLoadExtensionFromUrl(): void {
    const routeInfo = this.getExtensionTypeFromRoute();

    if (!routeInfo) {
      this.extensionsService.resetExtensionState();
      return;
    }

    const { type, paramValue } = routeInfo;
    const usesIdInUrl = ExtensionsUsingIdInUrl.includes(type);

    if (usesIdInUrl) {
      const extensionId = parseInt(paramValue, 10);
      if (!isNaN(extensionId)) {
        this.loadExtensionById(extensionId);
      }
    } else {
      this.loadExtensionByUsageToken(paramValue);
    }
  }
  loadExtension(extensionId: number): void {
    const { metricsFromDate, metricsToDate } = this.getMetricsRange();
    this.store.dispatch(
      CheckoutActions.loadExtensionById({
        shopId: this.shopService.shopId,
        metricsToDate,
        metricsFromDate,
        extensionId,
      }),
    );
  }
  getMetricsRange(): { metricsFromDate: string; metricsToDate: string } {
    const metricsFromDate = new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().slice(0, 10);
    const metricsToDate = new Date().toISOString().slice(0, 10);
    return { metricsFromDate, metricsToDate };
  }
  subscribeToExtensionType(): void {
    this.extensionsService.currentExtensionType$.pipe(takeUntil(this.destroy$)).subscribe((type) => {
      this.type = type;
      this.enableMetrics = type ? CheckoutMetricsComponent.supportedExtensions.includes(type) : false;
      this.cdr.detectChanges();
    });
  }
  handleAddExtension(type: ExtensionComponentType): void {
    this.store.dispatch(CheckoutActions.resetActiveExtension());
    const routeSlug = TypeToRouteSlugMap[type];
    if (routeSlug) {
      this.router.navigate(['/convert/extensions', routeSlug, 'new']);
    }
  }
}
